import type {
  DashboardItem,
  LegacyWorkspaceConfig,
  WorkspaceFilterValue,
  WorkspaceConfig,
} from "@/types/workspaceConfig";

export const CURRENT_WORKSPACE_CONFIG_VERSION = "2.0.0";

const DEFAULT_PAGE_SIZE = 100;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

type WorkspaceFilterElement =
  | WorkspaceFilterPrimitive
  | WorkspaceFilterObject
  | WorkspaceFilterValue;

const isWorkspaceFilterElement = (
  value: unknown,
): value is WorkspaceFilterElement =>
  value === null ||
  ["string", "number", "boolean"].includes(typeof value) ||
  isPlainObject(value) ||
  (Array.isArray(value) && value.every(isWorkspaceFilterElement));

const isWorkspaceFilterValue = (
  value: unknown,
): value is WorkspaceFilterValue =>
  Array.isArray(value) && value.every(isWorkspaceFilterElement);

const normalizeWorkspaceFilters = (
  value: unknown,
): Record<string, WorkspaceFilterValue> => {
  if (!isRecord(value)) return {};

  return Object.fromEntries(
    Object.entries(value).filter(([, filterValue]) =>
      isWorkspaceFilterValue(filterValue),
    ),
  );
};

const serializeForComparison = (value: unknown): string => {
  const sortObjectKeys = (item: unknown): unknown => {
    if (Array.isArray(item)) return item.map(sortObjectKeys);
    if (!isRecord(item)) return item;

    return Object.fromEntries(
      Object.keys(item)
        .sort()
        // Treat a field that's absent the same as one explicitly set to
        // null/undefined (e.g. an omitted `screenView`) — otherwise an
        // already-canonical config that merely omits an optional field
        // would be flagged as needing migration.
        .filter((key) => item[key] !== undefined && item[key] !== null)
        .map((key) => [key, sortObjectKeys(item[key])]),
    );
  };

  return JSON.stringify(sortObjectKeys(value));
};

export function sanitizePlotRuntimeFields(
  plot: any,
  metadataColumns?: string[],
  metadataFilterKeys?: string[],
) {
  const plotWithoutRuntimeFields = { ...(plot || {}) };
  delete plotWithoutRuntimeFields.data;
  delete plotWithoutRuntimeFields.dataIsLoading;
  delete plotWithoutRuntimeFields.inModal;

  const configWithoutRuntimeFields = {
    ...(plotWithoutRuntimeFields.config || {}),
  };
  delete configWithoutRuntimeFields.metadata;
  // Not callbacks, so the sweep below does not reach them.
  delete configWithoutRuntimeFields.filterableColumns;
  delete configWithoutRuntimeFields.appliedFilters;
  delete configWithoutRuntimeFields.selectedIdName;
  delete configWithoutRuntimeFields.selectedIdValue;
  delete configWithoutRuntimeFields.hasPlotFilters;
  delete configWithoutRuntimeFields.inModal;

  // Every runtime callback the wrapper injects, named or not. A saved config is
  // JSON, so no function belongs in one -- and `createComparableWorkspaceState`
  // structuredClones this, which throws on the first one that gets through.
  for (const [key, value] of Object.entries(configWithoutRuntimeFields)) {
    if (typeof value === "function") delete configWithoutRuntimeFields[key];
  }

  const allowedKeys = metadataFilterKeys ?? metadataColumns;
  if (configWithoutRuntimeFields.plotFilters && allowedKeys) {
    const allowed = new Set(allowedKeys);
    configWithoutRuntimeFields.plotFilters = Object.fromEntries(
      Object.entries(configWithoutRuntimeFields.plotFilters).filter(([key]) =>
        allowed.has(key),
      ),
    );
  }

  return {
    ...plotWithoutRuntimeFields,
    config: configWithoutRuntimeFields,
  };
}

export function sanitizePlotsRuntimeFields(
  plots?: any[],
  metadataColumns?: string[],
  metadataFilterKeys?: string[],
) {
  return (plots || []).map((plot) =>
    sanitizePlotRuntimeFields(plot, metadataColumns, metadataFilterKeys),
  );
}

const normalizePagination = (value: unknown) => {
  const pagination = isRecord(value) ? value : {};
  const current =
    typeof pagination.current === "number" ? pagination.current : 1;
  const pageSize =
    typeof pagination.pageSize === "number"
      ? pagination.pageSize
      : DEFAULT_PAGE_SIZE;

  return { current, pageSize };
};

const normalizeDashboardItems = (value: unknown): DashboardItem[] => {
  if (!Array.isArray(value)) return [];

  return value.map((item) => {
    if (isRecord(item) && item.type === "plot" && "config" in item) {
      return {
        type: "plot",
        config: sanitizePlotRuntimeFields(item.config),
        layout: item.layout,
      } as DashboardItem;
    }

    return {
      type: "plot",
      config: sanitizePlotRuntimeFields(item),
    } as DashboardItem;
  });
};

/**
 * Accepts a workspace config in either the legacy flat shape or the current
 * tabbed shape (or anything malformed/unknown) and always produces the
 * canonical, current-version shape. Filter values that don't match the
 * expected `WorkspaceFilterValue` structure (e.g. corrupted persisted data)
 * are dropped rather than passed through, since downstream consumers assume
 * a validated shape.
 */
export function normalizeWorkspaceConfig(
  config: WorkspaceConfig | LegacyWorkspaceConfig | unknown,
): WorkspaceConfig {
  const source = (isRecord(config) ? config : {}) as WorkspaceConfig &
    LegacyWorkspaceConfig;
  const tabs = Array.isArray(source.tabs) ? source.tabs : [];
  const tableTab = tabs.find((tab) => isRecord(tab) && tab.type === "table");
  const dashboardTab = tabs.find(
    (tab) => isRecord(tab) && tab.type === "dashboard",
  );

  const normalizedTableConfig = {
    columns:
      isRecord(tableTab) &&
      isRecord(tableTab.config) &&
      Array.isArray(tableTab.config.columns)
        ? tableTab.config.columns
        : Array.isArray(source.columns)
          ? source.columns
          : [],
    lockedColumns:
      isRecord(tableTab) &&
      isRecord(tableTab.config) &&
      typeof tableTab.config.lockedColumns === "number"
        ? tableTab.config.lockedColumns
        : typeof source.lockedColumns === "number"
          ? source.lockedColumns
          : 0,
    sorting:
      isRecord(tableTab) &&
      isRecord(tableTab.config) &&
      Array.isArray(tableTab.config.sorting)
        ? tableTab.config.sorting
        : Array.isArray(source.sorting)
          ? source.sorting
          : [],
    pagination: normalizePagination(
      isRecord(tableTab) && isRecord(tableTab.config)
        ? tableTab.config.pagination
        : source.pagination,
    ),
  };

  const normalizedDashboardConfig = normalizeDashboardItems(
    isRecord(dashboardTab) ? dashboardTab.config : source.plots,
  );
  const rawGlobalFilters =
    isRecord(source.global) && "filters" in source.global
      ? source.global.filters
      : source.filters;

  return {
    global: {
      filters: normalizeWorkspaceFilters(rawGlobalFilters),
    },
    tabs: [
      {
        name:
          isRecord(tableTab) && typeof tableTab.name === "string"
            ? tableTab.name
            : "Table",
        type: "table",
        config: normalizedTableConfig,
      },
      {
        name:
          isRecord(dashboardTab) && typeof dashboardTab.name === "string"
            ? dashboardTab.name
            : "Dashboard",
        type: "dashboard",
        config: normalizedDashboardConfig,
      },
    ],
    screenView:
      typeof source.screenView === "string" ? source.screenView : null,
    version: CURRENT_WORKSPACE_CONFIG_VERSION,
  };
}

/**
 * True when persisting `normalizeWorkspaceConfig(config)` back would change
 * what's stored — i.e. `config` is still in a legacy or otherwise
 * non-canonical shape and should be migrated.
 */
export function workspaceConfigNeedsMigration(
  config: WorkspaceConfig | LegacyWorkspaceConfig | unknown,
) {
  return (
    serializeForComparison(normalizeWorkspaceConfig(config)) !==
    serializeForComparison(config)
  );
}
